/**
 * Section « Modèles de journée » de la Bibliothèque (étape 5).
 *
 * ⚠️ ARBITRAGE Bibliothèque / Réglages. Les specs se contredisent en apparence :
 * le §8.2 range « modèles de journée » sous **Bibliothèque**, le §6.8 range
 * « gestion des modèles de journée et du planning hebdomadaire » sous
 * **Réglages**. Tranché ainsi, et documenté dans CLAUDE.md :
 *
 *   - **Bibliothèque** = les *objets* du référentiel. Un modèle de journée est
 *     un objet réutilisable, au même titre qu'un aliment ou une recette (§8.2
 *     les cite d'ailleurs ensemble). On le crée, on l'édite, on le supprime ici.
 *   - **Réglages** = le *comportement* de l'app. Quel modèle s'applique quel
 *     jour est une préférence, pas un objet : le planning vit donc là-bas
 *     (§6.8), et son volet renvoie ici pour l'édition.
 *
 * ⚠️ Extraite de `LibraryScreen` aux retours de l'étape 10, quand la
 * Bibliothèque est passée en sous-onglets : les trois sections sont désormais
 * symétriques (`TemplatesSection`, `FoodsSection`, `RecipesSection`) et l'écran
 * ne fait plus que router entre elles.
 *
 * ⚠️ **Refonte** : la liste est la `.list-card` du prototype — une carte sans
 * rembourrage, des lignes filetées d'un bord à l'autre, le bouton de
 * suppression en pilule à droite. La logique (requêtes, mutations,
 * invalidations, éditeur) est inchangée.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { DayTemplateDTO, DayTemplateInput } from '../../../../shared/types';
import { readError } from '../../api/client';
import { createTemplate, deleteTemplate, fetchTemplates, updateTemplate } from '../../api/templates';
import { Button, Card } from '../../components/nourri';
import { ErrorNote, Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { TemplateEditor } from './TemplateEditor';
import { draftOf, emptyDraft, type TemplateDraft } from './templateDraft';

function g(value: number): string {
  return (Math.round(value * 10) / 10).toLocaleString('fr-FR');
}

/** Ce qui est en cours d'édition : une création, ou un modèle existant. */
type Editing = { mode: 'create' } | { mode: 'edit'; template: DayTemplateDTO };

function TemplateRow({
  template,
  onEdit,
  onDelete,
  deleting,
}: {
  template: DayTemplateDTO;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <li className="n-list__row">
      <button type="button" onClick={onEdit} className="n-list__main">
        <span className="n-list__name">{template.name}</span>
        <span className="n-list__meta tnum">
          {g(template.targets.protein)} P · {g(template.targets.carbs)} G ·{' '}
          {g(template.targets.fat)} L · {Math.round(template.targets.kcal).toLocaleString('fr-FR')}{' '}
          kcal · {template.meals.length} repas
        </span>
        {Math.round(template.kcalGap.delta) !== 0 && (
          <span className="n-list__meta tnum">
            écart 4/4/9 : {template.kcalGap.delta > 0 ? '+' : '−'}
            {Math.abs(Math.round(template.kcalGap.delta)).toLocaleString('fr-FR')} kcal
          </span>
        )}
      </button>
      <Button variant="danger" disabled={deleting} onClick={onDelete}>
        Supprimer
      </Button>
    </li>
  );
}

export function TemplatesSection() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [draft, setDraft] = useState<TemplateDraft>(emptyDraft);

  const templates = useQuery({ queryKey: ['templates'], queryFn: fetchTemplates });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['templates'] });
    void queryClient.invalidateQueries({ queryKey: ['planning'] });
    // Une journée déjà créée n'est pas concernée (§7.4), mais une journée
    // encore à créer le sera : on relit plutôt que de laisser un cache décider.
    void queryClient.invalidateQueries({ queryKey: ['day'] });
  };

  const save = useMutation({
    mutationFn: (input: DayTemplateInput) =>
      editing?.mode === 'edit'
        ? updateTemplate(editing.template.id, input)
        : createTemplate(input),
    onSuccess: () => {
      refresh();
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: refresh,
  });

  const openCreate = () => {
    setDraft(emptyDraft());
    setEditing({ mode: 'create' });
  };

  const openEdit = (template: DayTemplateDTO) => {
    setDraft(draftOf(template));
    setEditing({ mode: 'edit', template });
  };

  return (
    <div className="n-page">
      {templates.isPending && <Hint>Chargement…</Hint>}
      <ReadNote error={readError(templates)} quoi="la liste des modèles" />
      {save.isError && <ErrorNote>{save.error.message}</ErrorNote>}
      {remove.isError && <ErrorNote>{remove.error.message}</ErrorNote>}

      {templates.data?.length === 0 && (
        <p className="n-empty">
          Aucun modèle. Un modèle porte les objectifs P/G/L/kcal d’une journée et sa liste de
          repas ; le planning des Réglages décide ensuite quel jour il s’applique.
        </p>
      )}

      {templates.data && templates.data.length > 0 && (
        <Card padding="none">
          <ul className="n-list">
            {templates.data.map((template) => (
              <TemplateRow
                key={template.id}
                template={template}
                deleting={remove.isPending}
                onEdit={() => {
                  openEdit(template);
                }}
                onDelete={() => {
                  remove.mutate(template.id);
                }}
              />
            ))}
          </ul>
        </Card>
      )}

      {/* Prototype `.primary-btn` : pleine largeur, en pilule, sous la liste. */}
      <Button variant="primary" size="lg" block onClick={openCreate}>
        Nouveau modèle
      </Button>

      <p className="n-foot-note">
        Supprimer un modèle retire ses affectations de planning, mais ne touche à aucune journée
        déjà créée.
      </p>

      {editing && (
        <TemplateEditor
          // Une clé par cible d'édition : le brouillon interne repart de zéro
          // quand on passe d'un modèle à un autre.
          key={editing.mode === 'edit' ? `edit-${String(editing.template.id)}` : 'create'}
          open
          title={editing.mode === 'edit' ? `Modifier « ${editing.template.name} »` : 'Nouveau modèle'}
          draft={draft}
          onDraftChange={setDraft}
          saving={save.isPending}
          onSubmit={(input) => {
            save.mutate(input);
          }}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
